import { useEffect, useRef, useState } from "react";
import { HttpError } from "../api/errors";
import { useChatStream } from "../hooks/useChatStream";
import Composer from "./Composer";
import DeployButton from "./DeployButton";
import ForbiddenView from "./ForbiddenView";
import IssuesPanel from "./IssuesPanel";
import Message from "./Message";

type Tab = "chat" | "issues";

type Props = { project: string };

export default function ChatPanel({ project }: Props) {
  const {
    messages,
    send,
    stop,
    isStreaming,
    isLoadingHistory,
    authError: _authError,
    clearAuthError: _clearAuthError,
    error,
    clearError,
    loadLatestHistory,
    loadConversation,
    startNew,
  } = useChatStream(project);
  void _authError; void _clearAuthError;
  const [tab, setTab] = useState<Tab>("chat");

  // Listen for cross-component "load this conversation" events from Sidebar drawer (04-07).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ conversationId: number }>).detail;
      if (detail && typeof detail.conversationId === "number") {
        void loadConversation(detail.conversationId);
      }
    };
    window.addEventListener("pterocloud-ai:load-conversation", handler);
    return () => window.removeEventListener("pterocloud-ai:load-conversation", handler);
  }, [loadConversation]);

  // Load latest history on mount + project change. ChatPanel is also remounted
  // via key={selectedId} from the parent, so this fires fresh per project.
  useEffect(() => {
    void loadLatestHistory();
  }, [loadLatestHistory]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Esc to Stop the in-flight response. Common chat keybind.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isStreaming) {
        e.preventDefault();
        void stop();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isStreaming, stop]);

  const headerBusy = isStreaming || isLoadingHistory;
  const isForbidden = error instanceof HttpError && error.status === 403;

  // Forbidden takes precedence over the normal chat surface so a 403 from
  // history-load shows the empty-state immediately on project switch instead
  // of flashing the placeholder + error banner.
  if (isForbidden) {
    return (
      <div className="chat-panel">
        <div className="chat-header">
          <span className="chat-header-title">{project}</span>
        </div>
        <ForbiddenView projectId={project} />
      </div>
    );
  }

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <span className="chat-header-title">
          <span className="glyph glyph-addon" aria-hidden="true">
            {project.slice(0, 1).toUpperCase()}
          </span>
          {project}
        </span>
        <div className="chat-tabs">
          <button
            type="button"
            className={`chat-tab${tab === "chat" ? " active" : ""}`}
            onClick={() => setTab("chat")}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
            </svg>
            Chat
          </button>
          <button
            type="button"
            className={`chat-tab${tab === "issues" ? " active" : ""}`}
            onClick={() => setTab("issues")}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            Issues
          </button>
        </div>
        {tab === "chat" && isStreaming && (
          <span className="live" aria-live="polite">
            <span className="pulse" aria-hidden="true" /> streaming
          </span>
        )}
        {tab === "chat" && isStreaming && (
          <button
            type="button"
            className="stop-btn"
            onClick={() => void stop()}
            title="Stop the bot's current response"
          >
            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
            Stop
          </button>
        )}
        {tab === "chat" && (
          <button
            type="button"
            className="new-conversation-btn"
            onClick={() => void startNew()}
            disabled={headerBusy}
            title="Start a new conversation"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New conversation
          </button>
        )}
        {tab === "chat" && <DeployButton project={project} />}
      </div>

      {tab === "issues" ? (
        <IssuesPanel project={project} />
      ) : (
        <>
          {error && (
            <div className="error-banner" role="alert">
              <span>{error.message}</span>
              <button type="button" onClick={clearError}>
                Dismiss
              </button>
            </div>
          )}

          <div className="messages" ref={scrollRef}>
            {isLoadingHistory && messages.length === 0 && (
              <p className="placeholder">Loading history…</p>
            )}
            {!isLoadingHistory && messages.length === 0 && (
              <p className="placeholder">No messages yet. Say hi to {project}.</p>
            )}
            {messages.map((m) => (
              <Message key={m.id} message={m} />
            ))}
          </div>

          <Composer disabled={isStreaming || isLoadingHistory} onSend={send} />
        </>
      )}
    </div>
  );
}
